import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import authContext from '../../context/authContext';
import TopHeader from '../../components/layouts/TopHeader';
import { ApiConstants } from '../../constants/ApiConstants';
import HouseholdMembers from './HouseholdMembers';
import PullToRefresh from 'react-simple-pull-to-refresh';
import Skeleton from '../../components/ui/Skeleton';
import { getPriceHelper, hasAnySubscription, minutesToHour, setDateHelper, getBudgetPeriodDates } from '../../utils/Helper';
import { ApiTypes } from '../../constants/Constants';
import ChoresGraph from './ChoresGraph';
import ExpensesGraph from './ExpensesGraph';
import PageLayout from '../../components/ui/PageLayout';
import DashboardHeader from '../../components/layouts/DashboardHeader';
import chevRght from '../../assets/images/chev-right.svg'
import useMakeRequest from '../../utils/ApiHelper';
import moment from 'moment';
import justivideLogoIcon from "../../assets/images/Justivide-PrimaryIcon.svg"
import { Routes } from '../../constants/RouteConstants';
import CookieHelper from '../../utils/CookieHelper';


const Dashboard = () => {
    let profile: any = useContext(authContext);
    const navigate = useNavigate();
    const dashboardSummary = useMakeRequest();
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
        totalHours: false,
        totalExpenses: false,
        totalPaid: false,
        totalUnpaid: false,
        currentBalances: false,
        finalizedBalances: false
    });

    const [dashboardSummaryData, setDashboardSummaryData] = useState<any>({})
    const [dashboardSummaryMemberData, setDashboardSummaryMemberData] = useState<any>({})

    const handleRefresh = async () => {
        return new Promise(async (resolve: any) => {
            await profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET })
            resolve();
        });
    }

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }

    useEffect(() => {
        profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET })

        // Example: Store last visit time in a cookie
        CookieHelper.set('last_dashboard_visit', new Date().toISOString());

        // Log the last visit if it exists
        const lastVisit = CookieHelper.get('last_dashboard_visit');
        if (lastVisit) {
            console.log('Last visit was at:', lastVisit);
        }
    }, [])

    useEffect(() => {
        if (profile?.data?.hh_member?.household?.id) {

            const { startDate, endDate } = getBudgetPeriodDates(
                moment().year(),
                moment().month(),
                profile?.data?.hh_member?.household?.billing_date || 1
            );

            dashboardSummary.makeRequest({
                url: ApiConstants?.DashboardSummaryGet,
                payload: {
                    hh_id: profile?.data?.hh_member?.household?.id,
                    start_date: startDate,
                    end_date: endDate
                },
                type: ApiTypes.POST
            })
        }
    }, [profile?.data?.hh_member?.household?.id])

    useEffect(() => {
        if (dashboardSummary?.data?.data?.summary) {
            setDashboardSummaryData(dashboardSummary?.data?.data?.summary)
            setDashboardSummaryMemberData(dashboardSummary?.data?.data?.members)
        }
    }, [dashboardSummary?.data?.data?.summary])

    const currentBalance = typeof profile?.data?.other_params === "string" ? JSON.parse(profile?.data?.other_params) : profile?.data?.other_params

    const hasSubscription = hasAnySubscription(profile?.data)
    return (
        <PageLayout className='main-page' showHeader={false}>
            {/* Balance Card */}
            {profile?.loading ? (
                <Skeleton className='balance-card-skeleton mb-4' />
            ) : (
                <div className='balance-card mb-4'>
                    <DashboardHeader />
                    <div className='balance-amount'>
                        {getPriceHelper(currentBalance?.balance || 0)}
                    </div>
                    <div className='balance-label'>
                        Current Balance
                    </div>
                </div>
            )}

            <div className='page-padding-x'>

                {/* Members Section */}
                {profile?.data?.hh_member && (
                    <HouseholdMembers profileData={profile} />
                )}
            </div>
            {/* Chores Graph */}
            {profile?.data?.hh_member && (
                <div className='mb-4'>
                    <ChoresGraph />
                </div>
            )}

            {/* Expenses Graph */}
            {/* {profile?.data?.hh_member && (
                <ExpensesGraph />
            )} */}

            {/* Summary Section */}
            {profile?.data?.hh_member && (
                <div className='summary-section'>
                    <h3 className='summary-title'>Summary</h3>

                    {/* Total Hours Completed */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                        role='button'
                        aria-expanded={expandedSections.totalHours}
                        onClick={() => toggleSection('totalHours')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSection('totalHours');
                            }
                        }}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Total Hours Completed</span>
                                <span className='summary-item-arrow'>
                                    <img className={`accordion-arrow ${expandedSections.totalHours ? 'expanded' : ''}`} src={chevRght} alt="" />
                                </span>
                            </div>
                            <span className='summary-item-value'>
                                {dashboardSummaryData?.total_hours_completed || 0}
                            </span>
                        </div>
                    </div>
                    {expandedSections.totalHours && (
                        <div className="summary-item-expanded">
                            {dashboardSummaryMemberData?.map((member: any) => (
                                <div className="summary-sub-item" key={member.user_id}>
                                    <span className="summary-sub-label">
                                        {member.name}
                                    </span>
                                    <span className="summary-sub-value">
                                        {Number(member.chores_hours_completed).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Total Hours Projected */}
                    <div className='summary-item' tabIndex={0}>
                        <div className='summary-item-header'>
                            <span className='summary-item-label'>Total Hours Projected</span>
                            <span className='summary-item-value'>{dashboardSummaryData?.total_hours_projected || 0}</span>
                        </div>
                    </div>

                    {/* Total Expenses */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Total Expenses</span>
                            </div>
                            <span className='summary-item-value'>
                                ${dashboardSummaryData?.total_expenses || 0}
                            </span>
                        </div>
                    </div>

                    {/* Total Paid Expenses */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                        role='button'
                        aria-expanded={expandedSections.totalPaid}
                        onClick={() => toggleSection('totalPaid')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSection('totalPaid');
                            }
                        }}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Total Paid Expenses</span>
                                <span className='summary-item-arrow'>
                                    <img className={`accordion-arrow ${expandedSections.totalPaid ? 'expanded' : ''}`} src={chevRght} alt="" />
                                </span>
                            </div>
                            <span className='summary-item-value'>
                                ${dashboardSummaryData?.total_paid_expenses || 0}
                            </span>
                        </div>
                    </div>

                    {expandedSections.totalPaid && (
                        <div className="summary-item-expanded">
                            {dashboardSummaryMemberData?.map((member: any) => (
                                <div className="summary-sub-item" key={member.user_id}>
                                    <span className="summary-sub-label">
                                        {member.name}
                                    </span>
                                    <span className="summary-sub-value">
                                        {Number(member.expenses_paid).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Total Unpaid Expenses */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Total Unpaid Expenses</span>
                            </div>
                            <span className='summary-item-value'> ${dashboardSummaryData?.total_unpaid_expenses || 0}</span>
                        </div>
                    </div>

                    {/* Current Balances */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                        role='button'
                        aria-expanded={expandedSections.currentBalances}
                        onClick={() => toggleSection('currentBalances')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSection('currentBalances');
                            }
                        }}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Current Balances</span>
                                <span className='summary-item-arrow'>
                                    <img className={`accordion-arrow ${expandedSections.currentBalances ? 'expanded' : ''}`} src={chevRght} alt="" />
                                </span>
                            </div>
                            <span className='summary-item-value'>${dashboardSummaryData?.current_balance}</span>
                        </div>
                    </div>

                    {expandedSections.currentBalances && (
                        <div className="summary-item-expanded">
                            {dashboardSummaryMemberData?.map((member: any) => (
                                <div className="summary-sub-item" key={member.user_id}>
                                    <span className="summary-sub-label">
                                        {member.name}
                                    </span>
                                    <span className="summary-sub-value">
                                        {Number(member.current_balance).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Finalized Balances */}
                    <div
                        className='summary-item'
                        tabIndex={0}
                        role='button'
                        aria-expanded={expandedSections.finalizedBalances}
                        onClick={() => toggleSection('finalizedBalances')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSection('finalizedBalances');
                            }
                        }}
                    >
                        <div className='summary-item-header'>
                            <div className='summary-item-left'>
                                <span className='summary-item-label'>Finalized Balances</span>
                                <span className='summary-item-arrow'>
                                    <img className={`accordion-arrow ${expandedSections.finalizedBalances ? 'expanded' : ''}`} src={chevRght} alt="" />
                                </span>
                            </div>
                            <span className='summary-item-value'>${dashboardSummaryData?.finalized_balance}</span>
                        </div>
                    </div>
                    {expandedSections.finalizedBalances && (
                        <div className="summary-item-expanded">
                            {dashboardSummaryMemberData?.map((member: any) => (
                                <div className="summary-sub-item" key={member.user_id}>
                                    <span className="summary-sub-label">
                                        {member.name}
                                    </span>
                                    <span className="summary-sub-value">
                                        {Number(member.current_balance).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}



            {/* No Data State */}
            {!profile?.data?.hh_member && (
                <>
                    <img className='w-25 rounded-3 mx-auto d-block' src={justivideLogoIcon} alt="" />
                    <h2 className="dashboard-welcome-text">
                        Welcome! Click{" "}
                        <Link to={Routes?.Subscription}>here</Link> to start a free trial or subscription.
                        <br />If you are already subscribed, please{" "}
                        <span
                            onClick={() => {
                                if (hasSubscription) {
                                    navigate(Routes?.AddHousehold);
                                }
                            }}
                            className="cursor-pointer text-decoration-underline"
                            style={{
                                color: '#5252f6',
                                fontWeight: 600,
                                pointerEvents: hasSubscription ? "auto" : "none",
                                opacity: hasSubscription ? 1 : 0.65,
                            }}
                        >
                            create a new household
                        </span>.
                    </h2>
                </>
            )}

        </PageLayout>
    )
}

export default Dashboard